import {Tooltip} from '@mantine/core';
import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import {BLUE_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {OverflowButton} from 'ui-library/menus/OverflowMenu';
import Tag, {TagColor} from 'ui-library/Tag';

import {
  isButtonActionCustomButton,
  isDataViewActionFunctionType,
  useDataViewContext,
} from './DataViewContext';
import getViewMetadataModalOverflowAction from './metadata-modal/getViewMetadataModalOverflowAction';

export default function DataViewToolbar(): JSX.Element {
  return (
    <div
      className={css(dataViewToolbarStyles.toolbar)}
      {...getTestProps({component: 'DataViewToolbar', element: 'root'})}
    >
      <DataViewWarningBadge />
      <DataViewActionButtons />
      <DataViewOverflowButton />
    </div>
  );
}

/** @package
 *
 * Define our own warnings instead of using Pane's built-in warnings because we want to show a badge with
 * text, rather than just an icon.
 * */
export function DataViewWarningBadge(): JSX.Element | null {
  const {warning} = useDataViewContext();

  if (warning == null) {
    return null;
  }

  const {label, hoverMessage} = warning;

  return (
    <span {...getTestProps({component: 'DataViewWarningBadge', element: 'root'})}>
      <Tooltip label={hoverMessage} disabled={hoverMessage == null}>
        <span>
          <Tag color={TagColor.WARN} icon="exclamation-triangle" text={label} />
        </span>
      </Tooltip>
    </span>
  );
}

/** @package */
export function DataViewActionButtons(): JSX.Element {
  const {
    actions: {buttonActions},
    actionInput,
  } = useDataViewContext();

  return (
    <>
      {buttonActions.map((buttonAction, index) => {
        if (isButtonActionCustomButton(buttonAction)) {
          if (buttonAction != null) {
            return <div key={index}>{buttonAction}</div>;
          } else {
            return null;
          }
        }

        let realizedButtonAction;
        if (isDataViewActionFunctionType(buttonAction)) {
          realizedButtonAction = buttonAction(actionInput);
        } else {
          realizedButtonAction = buttonAction;
        }
        if (realizedButtonAction == null) {
          return null;
        }

        const {icon, isAlternativeButtonColor, isDisabled, isToggled, helpText, label, onClick} =
          realizedButtonAction;
        return (
          <Tooltip label={helpText} disabled={helpText == null} key={index}>
            <span>
              <Button
                aria-label={label == null ? helpText : undefined}
                buttonStyle={isAlternativeButtonColor ? ButtonStyle.TERTIARY_BLUE : ButtonStyle.TERTIARY_GRAY}
                disabled={isDisabled}
                className={classNames(css(dataViewToolbarStyles.button), isToggled && 'is-active')}
                icon={icon}
                onClick={isDisabled ? undefined : onClick}
                size={ButtonSize.MINI}
                text={label}
              />
            </span>
          </Tooltip>
        );
      })}
    </>
  );
}

/** @package */
export function DataViewOverflowButton(): JSX.Element | null {
  const {
    actions: {overflowActions},
    actionInput,
    hideOverflowMenu,
    metadataContent,
    title,
  } = useDataViewContext();

  const realizedOverflowActions = useMemo(() => {
    // Skip realizing actions (which can invoke generator functions with side effects) and the
    // auto-injected metadata action entirely when the overflow menu is hidden.
    if (hideOverflowMenu) {
      return [];
    }
    return [
      ...overflowActions
        .map((overflowAction) => {
          if (typeof overflowAction === 'function') {
            return overflowAction(actionInput);
          }
          return overflowAction;
        })
        .filter((overflowAction) => overflowAction != null),
      getViewMetadataModalOverflowAction({
        title: `${title} metadata`,
        gridCells: [{component: title, label: 'Name'}, ...(metadataContent?.gridCells ?? [])],
        additionalContent: metadataContent?.additionalContent,
      }),
    ];
  }, [actionInput, hideOverflowMenu, metadataContent, overflowActions, title]);

  if (realizedOverflowActions.length === 0) {
    return null;
  }

  return (
    <Dropdown
      menu={
        <Menu dropLeft={true} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE}>
          {realizedOverflowActions.map(
            ({helpText, icon, isDisabled, isToggled, label, onClick, submenu}, index) => (
              <Option
                key={index}
                onSelect={isDisabled ? undefined : onClick}
                tooltipTitle={helpText}
                icon={icon}
                iconClassName={css(isToggled && dataViewToolbarStyles.toggledIcon)}
                isDisabled={isDisabled}
                dataTestAttrs={{
                  component: 'DataViewToolbarOverflowOption',
                  element: 'option',
                  index: index.toString(),
                }}
              >
                <div className={css(dataViewToolbarStyles.optionLabel)}>{label}</div>
                {submenu}
              </Option>
            )
          )}
        </Menu>
      }
    >
      <OverflowButton
        enableTooltip={true}
        className={css(dataViewToolbarStyles.button)}
        dataTestAttrs={{component: 'DataViewToolbarOverflowButton', element: 'button'}}
      />
    </Dropdown>
  );
}

/** @package */
export const dataViewToolbarStyles = StyleSheet.create({
  button: {
    marginLeft: 4,
  },
  toggledIcon: {
    color: BLUE_LIGHT,
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'row',
  },
  optionLabel: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
  },
});
